import React from 'react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { Palette, Image, FileText, Presentation, Layout, Megaphone, ArrowRight, ArrowLeft } from 'lucide-react';
import CorporateHeader from '../CorporateHeader';
import Footer from '../Footer';
import Breadcrumb from '../Breadcrumb';

const BrandInfrastructurePage = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: 'marketing',
      icon: Megaphone,
      title: 'Marketing Packages',
      description: 'Comprehensive marketing strategies tailored for authors, entrepreneurs, and brands. From launch campaigns to ongoing growth initiatives.',
      image: 'https://customer-assets.emergentagent.com/job_audrey-portfolio/artifacts/envuhssr_Marketing%20materials%20for%20THIS%20or%20THAT%20book%20series.png',
      items: ['Launch Campaigns', 'Social Media Strategy', 'Email Marketing', 'Paid Advertising'],
    },
    {
      id: 'visual-identity',
      icon: Palette,
      title: 'Visual Identity & Branding',
      description: 'Complete brand identity development. Logos, color systems, typography, and brand guidelines that command attention.',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/6wy8xieq_2Shareen%20logos.png',
      items: ['Logo Design', 'Brand Guidelines', 'Color Systems', 'Typography Selection'],
    },
    {
      id: 'event-design',
      icon: Presentation,
      title: 'Event Backdrops & Stage Design',
      description: 'Professional event visuals that elevate your presence. Conference backdrops, stage designs, and branded environments.',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/iw87f00s_SHAREEN%20BACKDROP.jpeg',
      items: ['Conference Backdrops', 'Stage Designs', 'Banner Systems', 'Photo Opportunities'],
    },
    {
      id: 'print',
      icon: FileText,
      title: 'Print Collateral',
      description: 'High-quality print materials that leave lasting impressions. Brochures, flyers, business cards, and promotional materials.',
      image: 'https://customer-assets.emergentagent.com/job_audrey-portfolio/artifacts/jem9ex7h_Marketing%20Now%20Offering%20Ghostwriting%20Services%20-%20flyer%281%29.png',
      items: ['Brochures', 'Flyers & Posters', 'Business Cards', 'Media Kits'],
    },
    {
      id: 'digital',
      icon: Layout,
      title: 'Digital Assets',
      description: 'Professional digital assets for your online presence. Social media graphics, web banners, and digital marketing materials.',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/3ov94vvn_TRANSFORM%20YOUR%20FINANCIAL%20FUTURE%20WITH%20CAMBIE%20JONES%20Log%20onto%20our%20website.png',
      items: ['Social Media Graphics', 'Web Banners', 'Email Templates', 'Digital Ads'],
    },
    {
      id: 'consulting',
      icon: Image,
      title: 'Strategic Consulting',
      description: 'Expert guidance on brand positioning, market strategy, and growth planning. One-on-one consulting for serious brand builders.',
      image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/i9lsqkrg_Learnology%20website.png',
      items: ['Brand Positioning', 'Market Analysis', 'Growth Strategy', 'Competitive Research'],
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      
      {/* Hero */}
      <section className="pt-28 pb-20 bg-gradient-to-b from-orange-900/30 via-black to-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-600/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <Breadcrumb items={[
            { label: 'Divisions', path: null },
            { label: 'Brand Infrastructure' }
          ]} />
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-amber-600 rounded-xl flex items-center justify-center">
              <Palette className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-orange-400 text-sm font-semibold uppercase tracking-wider">Division</p>
              <h1 className="text-4xl lg:text-5xl font-black text-white">Brand Infrastructure</h1>
            </div>
          </div>
          
          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
            Strategic marketing, design, and visual identity systems. Building the foundation for brands that command attention and drive results.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div 
                  key={service.id}
                  className="bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden hover:border-orange-600/50 transition-all duration-300 group"
                >
                  {/* Service Image */}
                  <div className="aspect-video overflow-hidden">
                    {service.image ? (
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-orange-900 to-amber-900 flex items-center justify-center">
                        <Icon className="w-16 h-16 text-white/30" />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-600 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-white">{service.title}</h3>
                    </div>
                    
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed">{service.description}</p>
                    
                    <ul className="space-y-1 mb-4">
                      {service.items.slice(0, 3).map((item) => (
                        <li key={item} className="text-gray-500 text-xs flex items-center">
                          <span className="w-1 h-1 bg-orange-500 rounded-full mr-2"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      onClick={() => navigate('/start-project')}
                      variant="outline"
                      size="sm"
                      className="w-full border-gray-700 text-gray-300 hover:border-orange-600 hover:text-orange-500 group-hover:border-orange-600 group-hover:text-orange-500"
                    >
                      Inquire
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Our Process</h2>
            <p className="text-gray-400">A streamlined approach to building your brand infrastructure.</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: '01', title: 'Discovery', desc: 'Understanding your vision, goals, and market position' },
              { step: '02', title: 'Strategy', desc: 'Developing a comprehensive brand and marketing plan' },
              { step: '03', title: 'Creation', desc: 'Designing and building your brand assets' },
              { step: '04', title: 'Launch', desc: 'Deploying your brand infrastructure to the world' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="text-5xl font-black text-orange-600/30 mb-4">{item.step}</div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Build Your Brand?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Let's discuss your brand infrastructure needs and create something extraordinary.
          </p>
          <Button 
            onClick={() => navigate('/start-project')}
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-8 py-4 text-lg"
          >
            Start Your Project
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default BrandInfrastructurePage;
